import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingHeader from '../../components/ui/MarketingHeader';
import PricingCard from './components/PricingCard';
import FeatureComparison from './components/FeatureComparison';
import TrustSignals from './components/TrustSignals';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PricingPlans = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const pricingPlans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for evaluation and small proof-of-concept projects',
      variant: 'free',
      cta: 'Start Free Trial',
      features: [
        '3 SAP Best Practice scope items',
        'Up to 10 questionnaire responses/month',
        'Basic document generation (BPD²/FSD²)',
        '1 project workspace',
        '1 team member',
        'Community support',
        '1GB document storage',
        '30-day version history'
      ],
      limitations: [
        'No AI assistance',
        'No digital signatures',
        'No real-time collaboration',
        'Limited template customization'
      ]
    },
    {
      name: 'Pro',
      price: billingCycle === 'monthly' ? 49 : 490,
      period: billingCycle === 'monthly' ? 'month' : 'year',
      description: 'Ideal for active SAP implementation projects and small teams',
      variant: 'pro',
      cta: 'Start Pro Trial',
      features: [
        'All SAP Best Practice scope items',
        'Unlimited questionnaire responses',
        'Advanced document templates',
        'Full AI assistance with context engine',
        'Up to 10 team members',
        '5 project workspaces',
        'Digital signature workflows',
        'Real-time collaboration',
        'Fit/Gap kanban board',
        'RICEFW tracker with effort estimation',
        'Draw.io diagram integration',
        'Email support (24h response)',
        '10GB document storage',
        '1-year version history'
      ]
    },
    {
      name: 'Enterprise',
      price: null,
      period: 'custom',
      description: 'Comprehensive solution for organization-wide SAP implementations',
      variant: 'enterprise',
      cta: 'Contact Sales',
      features: [
        'Everything in Pro, plus:',
        'Unlimited team members & projects',
        'Custom SAP Best Practice scope items',
        'Priority AI assistance with custom models',
        'SSO integration (SAML, OIDC)',
        'Advanced security & compliance',
        'Custom document templates & branding',
        'API access & webhook integrations',
        'DocuSign integration',
        'Dedicated customer success manager',
        'Phone support (4h response)',
        'Custom training & implementation',
        'Unlimited document storage',
        'Unlimited version history',
        'Data residency options',
        '99.9% uptime SLA'
      ]
    }
  ];

  const handleSelectPlan = (plan) => {
    if (plan?.variant === 'enterprise') {
      // Navigate to contact sales or open contact form
      window.location.href = 'mailto:sales@bpmlflows.com?subject=Enterprise Plan Inquiry';
    } else {
      // Navigate to registration with plan parameter
      navigate('/user-registration', { 
        state: { selectedPlan: plan?.variant } 
      });
    }
  };

  const handleGetStarted = () => {
    navigate('/user-registration');
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Choose Your Plan
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
              Select the perfect plan for your SAP implementation needs. Start with our free tier 
              and scale as your requirements grow.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-16">
              <span className={`text-sm font-medium ${
                billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative w-12 h-6 rounded-full nav-transition ${
                  billingCycle === 'yearly' ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 nav-transition ${
                  billingCycle === 'yearly' ? 'left-6' : 'left-0.5'
                }`} />
              </button>
              <span className={`text-sm font-medium ${
                billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-semibold">
                  Save 17%
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {pricingPlans?.map((plan, index) => (
                <PricingCard
                  key={plan?.name}
                  plan={plan}
                  isPopular={index === 1}
                  onSelectPlan={handleSelectPlan}
                />
              ))}
            </div>

            {/* Additional CTA */}
            <div className="text-center mt-16">
              <p className="text-muted-foreground mb-6">
                Not sure which plan is right for you? Start with our free tier and upgrade anytime.
              </p>
              <Button
                variant="outline"
                onClick={handleGetStarted}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-7xl mx-auto">
            <FeatureComparison />
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <TrustSignals />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-7xl mx-auto">
            <FAQSection />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-primary rounded-2xl p-12 text-primary-foreground">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Transform Your SAP Implementation?
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8">
                Join thousands of SAP architects who trust BPMLflows to accelerate their projects.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  onClick={handleGetStarted}
                  iconName="Rocket"
                  iconPosition="left"
                  className="text-lg px-8 py-3"
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:sales@bpmlflows.com'}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="text-lg px-8 py-3 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                >
                  Talk to Sales
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <Icon name="Workflow" size={20} className="text-accent-foreground" />
                </div>
                <span className="text-xl font-semibold">BPMLflows</span>
              </div>
              <p className="text-secondary-foreground/80 mb-4 max-w-md">
                Streamlining SAP Best Practices implementation through intelligent requirement 
                gathering and automated document generation.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-secondary-foreground/60 hover:text-secondary-foreground">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-secondary-foreground/60 hover:text-secondary-foreground">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-secondary-foreground/60 hover:text-secondary-foreground">
                  <Icon name="Github" size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-secondary-foreground/80">
                <li><a href="#" className="hover:text-secondary-foreground">Features</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Pricing</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Security</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Integrations</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-secondary-foreground/80">
                <li><a href="#" className="hover:text-secondary-foreground">Documentation</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Help Center</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Contact Us</a></li>
                <li><a href="#" className="hover:text-secondary-foreground">Status Page</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-secondary-foreground/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-secondary-foreground/60 text-sm">
              © {new Date()?.getFullYear()} BPMLflows. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-secondary-foreground/60 mt-4 md:mt-0">
              <a href="#" className="hover:text-secondary-foreground">Privacy Policy</a>
              <a href="#" className="hover:text-secondary-foreground">Terms of Service</a>
              <a href="#" className="hover:text-secondary-foreground">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPlans;